import React, { ReactElement, Ref } from 'react';

import { defaultLabelGetter } from '../../helpers/defaultGetters';
import { defaultRenderItem } from '../../helpers/defaultRenderers';
import useAutocompleteControl from '../../hooks/useAutocompleteControl';
import {
  ControlContainer,
  type ControlContainerAutocompleteProps,
  SearchInput,
  ControlProps,
} from '../../layout';
import { GenericItem, GetInputProps } from '../../types';

export type AutoCompleteControlProps<TItem extends GenericItem> = Omit<
  ControlContainerAutocompleteProps<TItem>,
  'as' | 'renderPlaceholder' | 'renderInput' | 'isAutocomplete'
> &
  Pick<
    ControlProps<TItem>,
    'selectedItem' | 'itemToString' | 'placeholder' | 'renderItem'
  > & {
    getInputProps: GetInputProps;
    openMenu?: () => void;
    softLimit?: number;
    maxLength?: number;
    showCount?: boolean;
    countDisplayThreshold?: number;
    searchPlaceholder?: string;
  };

const AutocompleteControlWrapped = <TItem extends GenericItem>(
  {
    isOpen,
    getInputProps,
    disabled,
    placeholder: placeholderProp,
    searchPlaceholder: searchPlaceholderProp,
    itemToString = defaultLabelGetter,
    selectedItem,
    openMenu,
    renderItem = defaultRenderItem,
    softLimit,
    maxLength,
    showCount,
    countDisplayThreshold,
    ...props
  }: AutoCompleteControlProps<TItem>,
  ref: Ref<HTMLInputElement>,
) => {
  const { containerProps, searchInputProps } = useAutocompleteControl({
    placeholderProp,
    searchPlaceholderProp,
    isOpen,
    disabled,
    openMenu,
    selectedItem,
  });

  const showItemCount = !isOpen && selectedItem;

  return (
    <ControlContainer
      ref={ref as Ref<HTMLDivElement>}
      {...containerProps}
      {...props}
    >
      {showItemCount && renderItem({ selectedItem, itemToString, isOpen })}
      <SearchInput
        style={{ ...(showItemCount && { display: 'none' }) }}
        isOpen={isOpen}
        {...getInputProps?.(searchInputProps)}
        showIcon={isOpen}
        softLimit={softLimit}
        maxLength={maxLength}
        showCount={showCount}
        countDisplayThreshold={countDisplayThreshold}
      />
    </ControlContainer>
  );
};

const AutocompleteControl = React.forwardRef(AutocompleteControlWrapped);

AutocompleteControl.displayName = 'AutocompleteControl';

export default AutocompleteControl as <T extends GenericItem>(
  p: AutoCompleteControlProps<T> & {
    ref?: Ref<HTMLDivElement>;
  },
) => ReactElement;
